/**
 * brand-routes.ts — which routes in mobile/app/ follow which brand pane.
 *
 * ─────────────────────────────────────────────────────────────────────
 * IMPORTANT — KEEP THIS FILE UP TO DATE
 * ─────────────────────────────────────────────────────────────────────
 * Whenever you add or rename a route under `mobile/app/`, append it to
 * the correct pane below. This file powers the route overlay on the
 * brand page (`/brand`), which Tyler uses to verify which screens
 * follow which visual standard. If routes here drift from reality, the
 * overlay misleads.
 *
 * Convention:
 *  - General  → marketing site + utility pages (no portal)
 *  - Student  → student portal surfaces
 *  - Client   → client (company) portal surfaces
 *  - Admin    → admin / staff portal surfaces
 *  - Judge    → judge scoring portal surfaces
 *
 * Auth pages (signup/login) live under General by default — they share
 * the marketing visual language.
 *
 * Portals not yet promoted to top-level panes (Manager, Campus) are
 * intentionally omitted. Add a new pane to brand.tsx + this file if
 * they need their own visual standard.
 */

export type PaneKey = "General" | "Student" | "Client" | "Admin" | "Judge";

export type RouteEntry = {
  /** URL as a user would type or share it. */
  path: string;
  /** Brief description of what the screen does. */
  description?: string;
};

export const ROUTE_MAP: Record<PaneKey, RouteEntry[]> = {
  General: [
    { path: "/", description: "Marketing landing" },
    { path: "/pricing", description: "Pricing page" },
    { path: "/faq", description: "FAQ" },
    { path: "/contact", description: "Contact form / book intake call" },
    { path: "/privacy", description: "Privacy policy" },
    { path: "/terms", description: "Terms of service" },
    { path: "/accept-invite", description: "Accept-invite landing" },
    { path: "/brand", description: "This page — internal brand reference" },
    { path: "/login", description: "Login" },
    { path: "/login/mfa-challenge", description: "MFA challenge during login" },
    { path: "/forgot-password", description: "Forgot password" },
    { path: "/reset-password", description: "Reset password" },
    { path: "/confirm", description: "Email confirmation" },
    { path: "/signup", description: "Signup hub" },
    { path: "/signup/student", description: "Student signup" },
    { path: "/signup/client", description: "Client (company) signup" },
    { path: "/signup/judge", description: "Judge signup" },
    { path: "/signup/employee", description: "Internal employee signup" },
  ],
  Student: [
    { path: "/student", description: "Student home / dashboard" },
    { path: "/student/cases", description: "Cases (browse / my cases)" },
    { path: "/student/find", description: "Find cases — tier hub" },
    { path: "/student/find/[tier]", description: "Find cases by tier" },
    { path: "/student/find/competition/[id]", description: "Case detail / register" },
    { path: "/student/teams", description: "Teams browser" },
    { path: "/student/team/[id]", description: "Team detail" },
    { path: "/student/career-interests", description: "Career interests intake" },
    { path: "/student/offers", description: "Interview offers" },
    { path: "/student/winnings", description: "Prize winnings" },
    { path: "/student/tax-info", description: "Tax info (W-9)" },
    { path: "/student/submissions", description: "My submissions" },
    { path: "/student/profile", description: "Student profile" },
    { path: "/student/notifications", description: "Notifications" },
    { path: "/student/competition/[id]", description: "Competition workspace" },
    { path: "/student/competition/[id]/info", description: "Competition info / brief" },
    { path: "/student/competition/[id]/deliverables", description: "Competition deliverables list" },
    { path: "/student/competition/[id]/team", description: "Competition team workspace" },
    { path: "/student/competition/[id]/teams/new", description: "Create a team" },
  ],
  Client: [
    { path: "/client", description: "Client portal home (programs grid)" },
    { path: "/client/welcome", description: "First-time welcome" },
    { path: "/client/candidates", description: "All candidates" },
    { path: "/client/company", description: "Your company" },
    { path: "/client/team", description: "Team list" },
    { path: "/client/team/[id]", description: "Team detail" },
    { path: "/client/billing", description: "Billing" },
    { path: "/client/settings", description: "Settings" },
    { path: "/client/notifications", description: "Notifications" },
    { path: "/client/student/[id]", description: "Student profile" },
    { path: "/client/competition/new", description: "New competition setup" },
    { path: "/client/competition/new/book", description: "Book intake call for new competition" },
    { path: "/client/competition/[id]", description: "Competition overview" },
    { path: "/client/competition/[id]/dashboard", description: "Competition live dashboard" },
    { path: "/client/competition/[id]/edit", description: "Edit competition / brief" },
    { path: "/client/competition/[id]/candidates", description: "Candidates in competition" },
    { path: "/client/competition/[id]/pipeline", description: "Pipeline view" },
    { path: "/client/competition/[id]/shortlist", description: "Shortlist" },
    { path: "/client/competition/[id]/positions", description: "Open positions" },
    { path: "/client/competition/[id]/deliverables", description: "Deliverables list" },
    { path: "/client/competition/[id]/deliverable/new", description: "New deliverable" },
    { path: "/client/competition/[id]/deliverable/[deliverableId]", description: "Deliverable detail" },
  ],
  Admin: [
    { path: "/admin", description: "Admin home / dashboard" },
    { path: "/admin/account", description: "Account & security" },
    { path: "/admin/analytics", description: "Analytics (super admin)" },
    { path: "/admin/announcements", description: "Announcements" },
    { path: "/admin/audit-log", description: "Audit log" },
    { path: "/admin/calendar", description: "Calendar" },
    { path: "/admin/campus-admins", description: "Campus admins" },
    { path: "/admin/case-approvals", description: "Case approval queue" },
    { path: "/admin/cases/[id]/complete", description: "Case completion" },
    { path: "/admin/cases/[id]/edit", description: "Case editor" },
    { path: "/admin/clients", description: "Clients list" },
    { path: "/admin/company/[id]", description: "Company detail" },
    { path: "/admin/competitions", description: "Competitions list" },
    { path: "/admin/competitions/[id]/winners", description: "Competition winners" },
    { path: "/admin/disbursements", description: "Disbursements" },
    { path: "/admin/domains", description: "Email domains" },
    { path: "/admin/inquiries", description: "Inbound inquiries" },
    { path: "/admin/invites", description: "Pending invites" },
    { path: "/admin/judge/[id]", description: "Judge detail" },
    { path: "/admin/judges", description: "Judges list" },
    { path: "/admin/mfa/enroll", description: "Admin MFA enrollment" },
    { path: "/admin/pending", description: "Pending review queue" },
    { path: "/admin/programs/new-rm", description: "New RM program" },
    { path: "/admin/promos", description: "Promo codes" },
    { path: "/admin/rejected", description: "Rejected items" },
    { path: "/admin/security", description: "Security" },
    { path: "/admin/sql-console", description: "SQL console (super admin, read-only)" },
    { path: "/admin/student/[id]", description: "Student detail" },
    { path: "/admin/students", description: "Students list" },
    { path: "/admin/students/review", description: "Student review queue" },
    { path: "/admin/team", description: "Internal team" },
    { path: "/admin/users", description: "All users + roles" },
  ],
  Judge: [
    { path: "/judge", description: "Judge home / assignments queue" },
    { path: "/judge/competition/[id]", description: "Competition overview for judging" },
    { path: "/judge/competition/[id]/score/[submissionId]", description: "Score a submission" },
    { path: "/judge/student/[id]", description: "Student profile (read-only)" },
    { path: "/judge/profile", description: "Judge profile" },
    { path: "/judge/settings", description: "Settings & notification prefs" },
  ],
};

/**
 * Pane labels and one-line role descriptions for the overlay header.
 */
export const PANE_LABELS: Record<PaneKey, { label: string; role: string }> = {
  General: {
    label: "General",
    role: "Marketing site + auth + utility pages. Public-facing.",
  },
  Student: {
    label: "Student",
    role: "Logged-in student portal. Cases, teams, competition workspace.",
  },
  Client: {
    label: "Client",
    role: "Company-facing portal. Programs, candidates, shortlists, billing.",
  },
  Admin: {
    label: "Admin",
    role: "Internal staff. CRM, approvals, audit, configuration.",
  },
  Judge: {
    label: "Judge",
    role: "Scoring portal — judges review and score submissions.",
  },
};
